/**
 * CONTRACT-ADMIN matches COMPANY-ADMIN for most screens; exceptions (COMPANY-ADMIN only): Team, Contract, and CXO management.
 */
export const COMPANY_ADMIN_EQUIVALENT_ROLES = ["COMPANY-ADMIN", "CONTRACT-ADMIN"];

/** Match API variants: "CONTRACT-ADMIN", "CONTRACT ADMIN", "company_admin", etc. */
export const normalizeRoleString = (role) => {
  if (role == null || role === "") return "";
  return String(role).toUpperCase().replace(/[-_]/g, " ").replace(/\s+/g, " ").trim();
};

/**
 * Compact, separator-free key for equality checks across spacing/hyphen variants:
 * "Escalator 1" / "ESCALATOR-1" / "escalator1" all collapse to "ESCALATOR1",
 * "Owner" -> "OWNER", "USER" -> "USER". Use when comparing a dropdown-selected
 * role against a per-instance `role` string from the API.
 */
export const compactRoleKey = (role) =>
  normalizeRoleString(role).replace(/\s+/g, "");

/**
 * End-user-tier roles: a plain USER plus the per-obligation "hats" (Owner /
 * Escalator1-3). Their underlying account is a user, so on dashboards they read
 * `user_status` (not `admin_status`) and they are denied admin-only reports
 * (e.g. the Audit Report). See [[axios-user-role-maps-escalator-owner-to-user]].
 */
export const isUserTierRole = (role) => {
  const k = compactRoleKey(role);
  return (
    k === "USER" ||
    k === "OWNER" ||
    k === "ESCALATOR1" ||
    k === "ESCALATOR2" ||
    k === "ESCALATOR3"
  );
};

/**
 * TEAM-ADMIN is functionally identical to PROJECT-ADMIN — backend started
 * issuing the new "TEAM ADMIN" string, but all role-based navigation/permission
 * checks in the app were written against PROJECT-ADMIN. Map it through here so
 * a single equivalence keeps the rest of the code stable.
 *
 * Returns the canonical hyphenated form (e.g. "PROJECT-ADMIN") for known roles
 * and the upper-snake form of any unrecognised string.
 */
export const canonicalRole = (role) => {
  const n = normalizeRoleString(role);
  if (!n) return "";
  if (n === "TEAM ADMIN" || n === "PROJECT ADMIN") return "PROJECT-ADMIN";
  if (n === "SUPER USER") return "SUPER-USER";
  if (n === "COMPANY ADMIN") return "COMPANY-ADMIN";
  if (n === "CONTRACT ADMIN") return "CONTRACT-ADMIN";
  if (n === "CXO ADMIN") return "CXO-ADMIN";
  if (n === "CUSTOM USER") return "CUSTOM-USER";
  if (n === "USER") return "USER";
  return n.replace(/\s+/g, "-");
};

/**
 * Rank order used to sort roles consistently in role pickers.
 * Lower number = higher privilege/higher in the list.
 * TEAM-ADMIN shares PROJECT-ADMIN's rank because they are equivalent.
 */
const ROLE_RANK = {
  "SUPER-USER": 1,
  "COMPANY-ADMIN": 2,
  "CXO-ADMIN": 3,
  "PROJECT-ADMIN": 4,
  "CONTRACT-ADMIN": 5,
  "USER": 6,
};

export const getRoleRank = (role) => {
  const c = canonicalRole(role);
  if (c in ROLE_RANK) return ROLE_RANK[c];
  return 99;
};

/**
 * Sort a list of role strings by rank, deduping by canonical form so the
 * dropdown never shows the same role twice. The backend has been observed
 * to return duplicates (e.g. "SUPER-USER" twice) and to return TEAM-ADMIN
 * alongside PROJECT-ADMIN — both collapse to one entry here.
 * Keeps the first occurrence's original string for display.
 */
export const sortRolesByRank = (roles) => {
  if (!Array.isArray(roles)) return [];
  const seen = new Set();
  const unique = [];
  for (const r of roles) {
    if (r == null || r === "") continue;
    const key = canonicalRole(r);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(r);
  }
  return unique.sort((a, b) => {
    const ra = getRoleRank(a);
    const rb = getRoleRank(b);
    if (ra !== rb) return ra - rb;
    return String(a).localeCompare(String(b));
  });
};

/** True only for contract-admin role (not company-admin). Used to hide Team/Contract/CXO management routes & nav. */
export const isContractAdminEquivalent = (role) =>
  normalizeRoleString(role) === "CONTRACT ADMIN";

/** Session role from localStorage / auth (handles SUPER-USER, SUPER USER, etc.). */
export const isSuperUser = (role) => normalizeRoleString(role) === "SUPER USER";

export const isCompanyAdminEquivalent = (role) => {
  const n = normalizeRoleString(role);
  return n === "COMPANY ADMIN" || n === "CONTRACT ADMIN";
};

/** Delegation menu/routes hidden for contract-admin, cxo-admin and custom-user sessions. */
export const isDelegationBlockedForRole = (role) => {
  const n = normalizeRoleString(role);
  return n === "CONTRACT ADMIN" || n === "CUSTOM USER" || n === "CXO ADMIN";
};

/** CXO ADMIN — see same nav as COMPANY ADMIN minus Delegation + Due Date Manager, with action controls disabled (read-only). */
export const isCxoAdminEquivalent = (role) =>
  normalizeRoleString(role) === "CXO ADMIN";

/** True for roles that should render action controls (checkbox/upload/download/edit/delete/approve) as disabled. */
export const isReadOnlyRole = (role) => isCxoAdminEquivalent(role);

/** For filters: SUPER-USER or any company-admin-equivalent role on a company row */
export const isSuperUserOrCompanyAdminEquivalent = (role) => {
  const n = normalizeRoleString(role);
  return n === "SUPER USER" || n === "COMPANY ADMIN" || n === "CONTRACT ADMIN";
};

/**
 * Rows from org/list-company often omit `role`; use the logged-in session role for admin checks
 * so COMPANY-ADMIN / CONTRACT-ADMIN still see the company dropdown on management screens.
 */
export const resolveCompanyListRowRole = (row, sessionRole) => {
  if (row?.role != null && String(row.role).trim() !== "") return row.role;
  return sessionRole;
};

export const isCompanyListRowAllowedForSession = (row, sessionRole) =>
  isSuperUserOrCompanyAdminEquivalent(resolveCompanyListRowRole(row, sessionRole));

const companyRowToOption = (e) => ({
  key: e.id,
  value: e.id,
  label: e.name || e.company_name || `Company ${e.id}`,
});

/**
 * Company options for admin/settings screens. Prefer role-filtered active companies; if that is empty,
 * fall back to all active rows from list-company (API is already scoped). If no active rows, use full list.
 */
export function getSettingsCompanySelectOptions(companyList, sessionRole) {
  if (!Array.isArray(companyList) || companyList.length === 0) return [];
  const active = companyList.filter((e) => e && e.status);
  const pool = active.length > 0 ? active : companyList.filter(Boolean);
  const roleFiltered = pool.filter((e) => isCompanyListRowAllowedForSession(e, sessionRole));
  const rows = roleFiltered.length > 0 ? roleFiltered : pool;
  return rows.map(companyRowToOption);
}

/** Sync localStorage + listCompany state from dropdown options (same pattern across settings pages). */
export function applySettingsCompanySelection(result, setListCompany) {
  if (!Array.isArray(result) || result.length === 0) {
    setListCompany({});
    return;
  }
  const stored = localStorage.getItem("company_selected");
  if (stored == null || stored === "") {
    localStorage.setItem("company_selected", String(result[0].key));
    localStorage.setItem("company_selected_name", result[0].label);
    setListCompany({ key: result[0].key, label: result[0].label, value: result[0].key });
    return;
  }
  const selected = parseInt(stored, 10);
  const found = result.find(
    (r) => parseInt(String(r.value), 10) === selected || String(r.key) === String(stored)
  );
  if (found) {
    setListCompany({ key: found.key, label: found.label, value: found.key });
  } else {
    localStorage.setItem("company_selected", String(result[0].key));
    localStorage.setItem("company_selected_name", result[0].label);
    setListCompany({ key: result[0].key, label: result[0].label, value: result[0].key });
  }
}
